import z, { object, number, literal, ZodIssueCode, preprocess } from 'zod';

import { IS_POSITIVE, IS_REQUIRED } from '@occc-app/lib/constants/validationMessages';
import { ProductGroupsSchema, withIsEnabled } from '@occc/validation';
import {
  LimitDisabledSchemaWithIsEnabled,
  LimitSchema,
  TransactionLimitDisabledSchemaWithIsEnabled,
  TransactionLimitSchema,
  transactionFrequencyLimitSchema,
  validateMembershipLessThanGlobal,
} from '@occc/feat-limits';
import { makeZodValidate as makeValidate } from '@occc-app/design-system';

import { VoucherStackingFormValuesSchema } from '@occc/feat-constraints';
import { BaseConfigurationSchema } from './base-configuration.schema';

import { AWARD_TYPES } from '../types';

const AwardCountEnabledSchema = object({
  membership: LimitSchema,
  global: LimitSchema,
  transaction: TransactionLimitSchema.optional(),
  transactionFrequency: transactionFrequencyLimitSchema,
}).superRefine((data, ctx) => {
  validateMembershipLessThanGlobal({
    membership: data.membership,
    global: data.global,
    ctx,
  });
});

const AwardCountDisabledSchema = object({
  membership: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  global: preprocess(() => ({}), LimitDisabledSchemaWithIsEnabled),
  transaction: preprocess(
    (value) => (value == null ? undefined : {}),
    TransactionLimitDisabledSchemaWithIsEnabled.optional(),
  ),
  transactionFrequency: preprocess(() => ({}), transactionFrequencyLimitSchema),
});

const AwardCountSchema = withIsEnabled(AwardCountEnabledSchema, AwardCountDisabledSchema);

const FuelRollbackEarningMechanismSchema = object({
  amount: number({ message: IS_REQUIRED }).gt(0, { message: IS_POSITIVE }),
  productGroups: ProductGroupsSchema,
});

export const FuelRollbackSchema = BaseConfigurationSchema.extend({
  awardType: literal(AWARD_TYPES.FUEL_ROLLBACK),
  fuelRollback: preprocess((val) => val ?? {}, FuelRollbackEarningMechanismSchema),
  awardCount: AwardCountSchema,
  voucherStackingConstraint: VoucherStackingFormValuesSchema,
}).superRefine((data, ctx) => {
  if (data.voucherStackingConstraint?.breakageRefund) {
    const { awardCount } = data;
    const isAwardCountEnabled = awardCount?.isEnabled === true;
    const isTransactionFulfillmentEnabled = awardCount?.transaction?.isEnabled === true;

    if (!isAwardCountEnabled) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: IS_REQUIRED,
        path: ['awardCount', 'isEnabled'],
      });
    }

    if (!isTransactionFulfillmentEnabled) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: IS_REQUIRED,
        path: ['awardCount', 'transaction', 'isEnabled'],
      });
    }

    if (isAwardCountEnabled && isTransactionFulfillmentEnabled && !awardCount?.transaction?.limit) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: IS_REQUIRED,
        path: ['awardCount', 'transaction', 'limit'],
      });
    }
  }
});

export type FuelRollbackFormValues = z.infer<typeof FuelRollbackSchema>;

export const validateFuelRollbackForm = makeValidate(FuelRollbackSchema);
